import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// redux
import {
  onCallReject,
  onCallHangUp,
  onCallAccept,
  saveCallLog,
  fetchLiveKitToken,
  onGroupCallInactive,
  toggleMinimizeCall,
} from 'src/store/slices/chatSlice';
// hooks
import { useChatEmitter } from 'src/hooks/useChatSocket';
// components
import ChatRoomGroupCall from './chat-room-group-call';
import ChatCallPip from './chat-call-pip';

// ----------------------------------------------------------------------

export default function ChatCallGlobal() {
  const dispatch = useDispatch();
  const { user } = useSelector((s) => s.auth);
  const { conversations, call } = useSelector((s) => s.chat);

  const { emitCallEvent } = useChatEmitter();

  // State for Document Picture-in-Picture (OS-level floating window)
  const [isPipOpen, setIsPipOpen] = useState(false);
  // Store the actual window object for PiP
  const [pipWindow, setPipWindow] = useState(null);

  /**
   * Toggle Document Picture-in-Picture.
   * This MUST be called from a direct user gesture (click).
   */
  const handleTogglePip = React.useCallback(async () => {
    if (isPipOpen) {
      if (pipWindow) pipWindow.close();
      setPipWindow(null);
      setIsPipOpen(false);
      return;
    }

    if (!window.documentPictureInPicture) return;

    try {
      const win = await window.documentPictureInPicture.requestWindow({
        width: 400,
        height: 450,
      });

      // Handle manual window close
      win.addEventListener('pagehide', () => {
        setPipWindow(null);
        setIsPipOpen(false);
      });

      setPipWindow(win);
      setIsPipOpen(true);
    } catch (err) {
      console.error('Failed to open PiP window:', err);
    }
  }, [isPipOpen, pipWindow]);

  // Auto-minimize when tab is hidden (multitasking support)
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      // If the tab is hidden and the call is full-screen, minimize it.
      // We check !isPipOpen because if they already have a floating window, they don't need a popover too.
      if (document.visibilityState === 'hidden' && call.active && !call.minimized && !isPipOpen) {
        dispatch(toggleMinimizeCall());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [call.active, call.minimized, isPipOpen, dispatch]);

  // Track call start time for call log duration
  const startTimeRef = React.useRef(null);

  // Set start time when call connects
  React.useEffect(() => {
    if (call.active && call.status === 'connected' && !startTimeRef.current) {
      startTimeRef.current = Date.now();
    }
    if (!call.active) {
      startTimeRef.current = null;
      if (pipWindow) pipWindow.close();
      setPipWindow(null);
      setIsPipOpen(false); // Reset PiP when call ends
    }
  }, [call.active, call.status, pipWindow]);

  /**
   * Accept an incoming call (both 1-on-1 and group).
   * Fetch our own LiveKit token and join the room.
   */
  const handleAcceptCall = React.useCallback(async () => {
    if (!call.conversationId) return;

    try {
      const res = await dispatch(fetchLiveKitToken({ conversationId: call.conversationId })).unwrap();

      // Notify caller we accepted (1-on-1 only)
      if (!call.isGroup && call.caller) {
        emitCallEvent('call:accept', { to: call.caller });
      }

      // Single dispatch — token + connected status together
      dispatch(onCallAccept({ token: res.token, url: res.url }));
      startTimeRef.current = Date.now();
    } catch (err) {
      console.error('Failed to accept call:', err);
      dispatch(onCallReject());
    }
  }, [call.conversationId, call.isGroup, call.caller, dispatch, emitCallEvent]);

  /**
   * Leave/end a call locally.
   */
  const handleCallLeave = React.useCallback(() => {
    if (!call.isGroup) {
      // Notify the other party the call ended
      const targetId = call.caller === user.id ? call.callee : call.caller;
      if (targetId) emitCallEvent('call:hang-up', { to: targetId });
    } else {
      // Clear this user's own "ongoing call" banner immediately
      dispatch(onGroupCallInactive({ conversationId: call.conversationId }));
    }

    // Save call log
    if (call.status === 'connected' && startTimeRef.current) {
      const duration = Math.floor((Date.now() - startTimeRef.current) / 1000);
      dispatch(saveCallLog(call.conversationId, { duration, callType: call.type, status: 'answered' }));
    } else if (call.status === 'calling' || call.status === 'ringing') {
      dispatch(saveCallLog(call.conversationId, {
        duration: 0,
        callType: call.type,
        status: call.receiving ? 'rejected' : 'missed',
      }));
    }

    startTimeRef.current = null;
    if (pipWindow) pipWindow.close();
    setPipWindow(null);
    dispatch(onCallHangUp());
  }, [call, dispatch, emitCallEvent, user.id, pipWindow]);

  // Memoized: only recomputes when the call's conversationId changes.
  const callConversation = React.useMemo(
    () => conversations.byId[call.conversationId] || null,
    [conversations.byId, call.conversationId]
  );

  if (!call.active) return null;

  return (
    <ChatRoomGroupCall
      open={call.active}
      onClose={handleCallLeave}
      onMinimize={() => dispatch(toggleMinimizeCall())}
      minimized={call.minimized}
      onAccept={handleAcceptCall}
      token={call.liveKitToken}
      url={call.liveKitUrl}
      conversationId={call.conversationId}
      callType={call.type}
      isIncoming={call.receiving}
      conversation={callConversation}
      // PiP specific props
      isPip={isPipOpen}
      pipWindow={pipWindow}
      onTogglePip={handleTogglePip}
      onPipClose={() => setIsPipOpen(false)}
    />
  );
}
